/**
 * Client-side decryption utilities for OTEL encrypted telemetry data.
 * Uses Web Crypto API exclusively — no external dependencies.
 *
 * Three-layer encryption (OMK envelope encryption):
 *   Per field:    base64( version[1] || nonce[12] || ciphertext || gcm_tag[16] )
 *   Wrapped DEK:  base64( nonce[12] || AES-GCM-encrypt(DEK, OMK) || tag[16] )
 *   Wrapped OMK:  base64( RSA_OAEP_SHA256_encrypt(OMK) )  — stored in DB per org
 *
 * Decryption chain:
 *   RSA private key → unwrap OMK → unwrap DEK → AES-GCM decrypt fields
 */

const ENCRYPTION_VERSION = 0x01;
const GCM_NONCE_SIZE = 12;
const VERSION_BYTE_SIZE = 1;
const GCM_TAG_BITS = 128;
const MIN_ENCRYPTED_LENGTH = VERSION_BYTE_SIZE + GCM_NONCE_SIZE + 16;

/**
 * Import an RSA private key from a PEM string.
 * Supports PKCS#8 (BEGIN PRIVATE KEY) format.
 */
export async function importPrivateKey(pemString) {
  const pemBody = pemString
    .replace(/-----BEGIN (?:RSA )?PRIVATE KEY-----/g, '')
    .replace(/-----END (?:RSA )?PRIVATE KEY-----/g, '')
    .replace(/\s/g, '');

  const binaryDer = base64ToArrayBuffer(pemBody);

  return crypto.subtle.importKey(
    'pkcs8',
    binaryDer,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['decrypt']
  );
}

/**
 * Unwrap the OMK using the user's RSA private key (RSA-OAEP decrypt).
 * The rsa_wrapped_omk comes from the data policy API response.
 * Returns the raw OMK as an ArrayBuffer (32 bytes).
 */
export async function unwrapOMK(rsaWrappedOmkBase64, rsaPrivateKey) {
  const wrappedBytes = base64ToArrayBuffer(rsaWrappedOmkBase64);

  return crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    rsaPrivateKey,
    wrappedBytes
  );
}

/**
 * Unwrap the per-batch DEK using the OMK (AES-256-GCM decrypt).
 * Wrapped DEK format: base64( nonce[12] || ciphertext || tag[16] )
 * Returns an AES-GCM CryptoKey for field decryption.
 */
export async function unwrapDEK(wrappedDekBase64, omk) {
  const data = base64ToUint8Array(wrappedDekBase64);

  // Import OMK as an AES-GCM CryptoKey
  const omkKey = await crypto.subtle.importKey(
    'raw',
    omk,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const nonce = data.slice(0, GCM_NONCE_SIZE);
  const ciphertextWithTag = data.slice(GCM_NONCE_SIZE);

  const rawDEK = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce, tagLength: GCM_TAG_BITS },
    omkKey,
    ciphertextWithTag
  );

  return crypto.subtle.importKey(
    'raw',
    rawDEK,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
}

/**
 * Decrypt a single AES-256-GCM encrypted field.
 * Expected format: base64( version[1] || nonce[12] || ciphertext || tag[16] )
 */
export async function decryptField(encryptedBase64, aesKey) {
  const data = base64ToUint8Array(encryptedBase64);

  if (data.length < MIN_ENCRYPTED_LENGTH) {
    throw new Error('Encrypted data too short');
  }

  const version = data[0];
  if (version !== ENCRYPTION_VERSION) {
    throw new Error(`Unsupported encryption version: ${version}`);
  }

  const nonce = data.slice(VERSION_BYTE_SIZE, VERSION_BYTE_SIZE + GCM_NONCE_SIZE);
  const ciphertextWithTag = data.slice(VERSION_BYTE_SIZE + GCM_NONCE_SIZE);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce, tagLength: GCM_TAG_BITS },
    aesKey,
    ciphertextWithTag
  );

  return new TextDecoder().decode(plaintext);
}

/**
 * Check whether a log record is encrypted based on its marker attributes.
 */
export function isEncryptedLog(logData) {
  if (!logData) return false;
  const markedEncrypted = (record) => {
    if (!record || typeof record !== 'object') return false;
    const flag = record.data_encrypted;
    const policy = record.data_policy;
    return flag === true
      || String(flag).trim().toLowerCase() === 'true'
      || (typeof policy === 'string' && policy.trim().toLowerCase().endsWith('_encrypted'));
  };
  return markedEncrypted(logData)
    || markedEncrypted(logData.attributes)
    || markedEncrypted(logData.custom_attributes)
    || markedEncrypted({
      data_encrypted: logData['attributes.data_encrypted'],
      data_policy: logData['attributes.data_policy'],
    });
}

export const ENCRYPTED_CONTENT_PLACEHOLDER = 'Content is encrypted';

/** A list/tooltip preview must never expose marked encrypted prompt/response bytes. */
export function getLogContentPreview(logData, fields, fallback = 'N/A') {
  if (isEncryptedLog(logData)) return ENCRYPTED_CONTENT_PLACEHOLDER;
  for (const field of fields) {
    const value = logData?.[field];
    if (value !== undefined && value !== null && String(value).trim()) return value;
  }
  return fallback;
}

/**
 * Extract the wrapped DEK from log data attributes.
 * Handles both flat dot-notation keys ('saf3ai.encryption.wrapped_key')
 * and nested object structure (attrs.saf3ai.encryption.wrapped_key) that
 * OpenSearch dynamic mapping may produce.
 */
export function getWrappedKey(logData) {
  if (!logData) return null;
  const flatKey = 'saf3ai.encryption.wrapped_key';

  // Check top-level flat key
  if (logData[flatKey]) return logData[flatKey];

  // Check attributes / custom_attributes
  for (const attrs of [logData.attributes, logData.custom_attributes]) {
    if (!attrs || typeof attrs !== 'object') continue;
    // Flat key in attrs
    if (attrs[flatKey]) return attrs[flatKey];

    // Nested object path: attrs.saf3ai.encryption.wrapped_key
    const nested = attrs.saf3ai?.encryption?.wrapped_key;
    if (nested) return nested;
  }

  return null;
}

/** Top-level fields that may contain encrypted content. */
const ENCRYPTED_TOP_FIELDS = [
  'prompt_full', 'prompt_short', 'response', 'response_text',
  'prompt', 'query', 'user_input', 'ai_response',
];

/** OTLP attribute keys that the gateway encrypts (mirrors SensitiveKeys in strip.go). */
const SENSITIVE_ATTR_KEYS = [
  'prompt', 'response',
  'gen_ai.prompt', 'gen_ai.response', 'gen_ai.user_input',
  'gen_ai.request.model_input', 'gen_ai.response.model_output',
  'security.scan.prompt', 'security.scan.response', 'security.scan.user_input',
  'security.scan.prompt.scanned', 'security.scan.response.scanned',
  'user_input', 'llm.prompt', 'llm.response',
  'gcp.vertex.agent.llm_request', 'gcp.vertex.agent.llm_response',
  'tool.result', 'callback.result', 'plugin.result',
];

/**
 * Decrypt all encrypted fields in a log data object.
 * Requires the OMK (already unwrapped from RSA) as an ArrayBuffer.
 * Returns a shallow copy with decrypted values.
 */
export async function decryptLogData(logData, omk) {
  if (!isEncryptedLog(logData)) return logData;

  const wrappedDekB64 = getWrappedKey(logData);
  if (!wrappedDekB64) {
    // Log is marked encrypted but has no wrapped key — this happens for
    // parent/orchestrator spans that carry the data_encrypted marker but
    // don't contain any encrypted content (e.g. agent.run_async spans).
    // Return the data as-is instead of throwing.
    return logData;
  }

  const dek = await unwrapDEK(wrappedDekB64, omk);
  const decrypted = { ...logData };

  // Decrypt top-level fields
  for (const field of ENCRYPTED_TOP_FIELDS) {
    if (decrypted[field] && typeof decrypted[field] === 'string') {
      try {
        decrypted[field] = await decryptField(decrypted[field], dek);
      } catch {
        // Field may not actually be encrypted (e.g. 'N/A' or plaintext)
      }
    }
  }

  // Decrypt nested attributes (handles both "attributes" and "custom_attributes")
  for (const attrKey of ['attributes', 'custom_attributes']) {
    if (!decrypted[attrKey]) continue;
    decrypted[attrKey] = { ...decrypted[attrKey] };
    for (const key of SENSITIVE_ATTR_KEYS) {
      // Try flat key first
      if (decrypted[attrKey][key] && typeof decrypted[attrKey][key] === 'string') {
        try {
          decrypted[attrKey][key] = await decryptField(decrypted[attrKey][key], dek);
        } catch {
          // Skip fields that fail to decrypt
        }
        continue;
      }
      // Try nested path (OpenSearch may store "llm.prompt" as {llm: {prompt: "..."}})
      const val = _getNestedValue(decrypted[attrKey], key);
      if (val && typeof val === 'string') {
        try {
          const plaintext = await decryptField(val, dek);
          _setNestedValue(decrypted[attrKey], key, plaintext);
        } catch {
          // Skip fields that fail to decrypt
        }
      }
    }
  }

  // Post-process: extract clean user messages from ADK/Gemini prompt blobs
  for (const field of ENCRYPTED_TOP_FIELDS) {
    if (decrypted[field] && typeof decrypted[field] === 'string') {
      decrypted[field] = _extractUserContent(decrypted[field]);
    }
  }

  return decrypted;
}

// --- Helpers ---

/**
 * Extract readable user content from a decrypted prompt/response string.
 *
 * ADK serialises the full GenerateContentConfig as the llm.prompt OTEL attribute.
 * After decryption the string looks like:
 *   model='gemini-2.5-flash' contents=[Content(parts=[Part(text='Hello')], role='user'), ...]
 *
 * This function pulls out just the conversation messages (user + model text parts)
 * and returns them as a clean, human-readable string. If the input doesn't match
 * the ADK format it is returned unchanged.
 */
function _extractUserContent(text) {
  if (!text || typeof text !== 'string') return text;

  // Quick check: does this look like an ADK GenerateContentConfig blob?
  if (!text.includes('contents=[Content(') && !text.includes("contents=[Content(")) {
    return text;
  }

  try {
    // Extract all Part(text='...') values with their roles
    const messages = [];
    // Match Content(..., role='user'|'model') blocks
    const contentRegex = /Content\(\s*parts=\[([\s\S]*?)\],\s*role='(user|model)'\s*\)/g;
    let match;
    while ((match = contentRegex.exec(text)) !== null) {
      const partsBlock = match[1];
      const role = match[2];

      // Extract text from Part(text='...') or Part(text="...")
      // The lookahead (?:\s*,|\s*\)) ensures we stop at the quote that's
      // followed by a comma or closing paren (not mid-value quotes).
      const partRegex = /Part\(\s*text=(['"])([\s\S]*?)\1(?:\s*,|\s*\))/g;
      let partMatch;
      while ((partMatch = partRegex.exec(partsBlock)) !== null) {
        const partText = partMatch[2]
          .replace(/\\n/g, '\n')
          .replace(/\\'/g, "'")
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
        if (partText.trim()) {
          messages.push({ role, text: partText.trim() });
        }
      }
    }

    if (messages.length === 0) return text;

    // Format as clean conversation
    return messages
      .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.text}`)
      .join('\n\n');
  } catch {
    return text;
  }
}

/** Resolve a dot-notation path on a nested object, e.g. "llm.prompt" → obj.llm.prompt */
function _getNestedValue(obj, dottedKey) {
  const parts = dottedKey.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

/** Set a value at a dot-notation path, creating intermediate objects as needed. */
function _setNestedValue(obj, dottedKey, value) {
  const parts = dottedKey.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (cur[parts[i]] == null || typeof cur[parts[i]] !== 'object') {
      cur[parts[i]] = {};
    }
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

/**
 * A red-team scan's encryption key, derived from the organization's data-policy OMK
 * (HKDF-SHA256 with a red-team label, so the OMK itself is never used as a scan key).
 * The same OMK always gives the same key, so a scan created with it can be read again by
 * anyone holding the data-policy private key. Returns the 32-byte key as base64.
 */
export async function deriveRedTeamScanKey(omk) {
  const base = await crypto.subtle.importKey('raw', omk, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: new TextEncoder().encode('saf3ai/redteam/scan-key/v1'),
    },
    base,
    256
  );
  let bin = '';
  new Uint8Array(bits).forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin);
}

function base64ToArrayBuffer(base64) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes.buffer;
}

function base64ToUint8Array(base64) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes;
}
